import { randomBytes } from '@exodus/crypto/randomBytes'
import * as sodium from '@exodus/crypto/sodium'
import {
  Fiat,
  PROVIDERS,
  getAssetsByPaymentMethod as getAssetsByPaymentMethod_,
} from '@exodus/fiat-client'
import { EXODUS_KEY_IDS } from '@exodus/key-ids'

import blockchainProvider, { getWidgetUrl as getBlockchainWidgetUrl } from './providers/blockchain'
import moonpayProvider, { getWidgetUrl as getMoonpayWidgetUrl } from './providers/moonpay'
import onramperProvider, { getWidgetUrl as getOnramperWidgetUrl } from './providers/onramper'
import rampProvider, { getWidgetUrl as getRampWidgetUrl } from './providers/ramp'
import robinhoodProvider, { getWidgetUrl as getRobinhoodWidgetUrl } from './providers/robinhood'
import sardineProvider, { getWidgetUrl as getSardineWidgetUrl } from './providers/sardine'
import xopayProvider, { getWidgetUrl as getXopayWidgetUrl } from './providers/xo-pay'
import paypalProvider, { getWidgetUrl as getPaypalWidgetUrl } from './providers/paypal'

class FiatRamp {
  #analyticsUserIdAtom
  #assetsModule
  #config
  #currencyAtom
  #fiatAssetsAtom
  #fiatAuthKeyPairAtom
  #fiatClient
  #fiatConfigAtom
  #fiatCountriesAtom
  #fiatOrdersAtom
  #fiatUserAtom
  #geolocationAtom
  #getProviderToken
  #fiatHiddenFailedOrderNotificationsAtom
  #keychain
  #logger
  #wallet
  #loaded
  #fetch
  #riskMetricsAtom
  #subscriptions = []

  client

  constructor({
    analyticsUserIdAtom,
    assetsModule,
    config,
    currencyAtom,
    fiatAssetsAtom,
    fiatAuthKeyPairAtom,
    fiatConfigAtom,
    fiatCountriesAtom,
    fiatOrdersAtom,
    fiatUserAtom,
    geolocationAtom,
    getBuildMetadata,
    fiatHiddenFailedOrderNotificationsAtom,
    keychain,
    wallet,
    logger,
    fetch,
    riskMetricsAtom,
    fiatClient = new Fiat({
      appId: config.APP_ID,
      fetch,
      metadata: getBuildMetadata,
      serverUrl: config.FIAT_SERVER_URL,
    }),
  }) {
    this.#analyticsUserIdAtom = analyticsUserIdAtom
    this.#assetsModule = assetsModule
    this.#config = config
    this.#currencyAtom = currencyAtom
    this.#fiatAssetsAtom = fiatAssetsAtom
    this.#fiatAuthKeyPairAtom = fiatAuthKeyPairAtom
    this.#fiatClient = fiatClient
    this.#fiatConfigAtom = fiatConfigAtom
    this.#fiatCountriesAtom = fiatCountriesAtom
    this.#fiatOrdersAtom = fiatOrdersAtom
    this.#fiatUserAtom = fiatUserAtom
    this.#geolocationAtom = geolocationAtom
    this.#fiatHiddenFailedOrderNotificationsAtom = fiatHiddenFailedOrderNotificationsAtom
    this.#fetch = fetch
    this.#keychain = keychain
    this.#logger = logger
    this.#wallet = wallet
    this.#riskMetricsAtom = riskMetricsAtom

    for (const method of [
      'addOrderEvent',
      'createOrderDeposit',
      'getOrders',
      'getPaymentMethods',
      'sign',
      'signUrl',
      'updateOrder',
    ]) {
      this[method] = this.#fiatClient[method].bind(this.#fiatClient)
    }
  }

  #getProviders = async (options) => {
    const fiatConfig = await this.#fiatConfigAtom.get()
    const availableProviders = {
      [PROVIDERS.blockchain]: blockchainProvider,
      [PROVIDERS.moonpay]: moonpayProvider,
      [PROVIDERS.onramper]: onramperProvider,
      [PROVIDERS.ramp]: rampProvider,
      [PROVIDERS.robinhood]: robinhoodProvider,
      [PROVIDERS.sardine]: sardineProvider,
      [PROVIDERS.xopay]: xopayProvider,
      [PROVIDERS.paypal]: paypalProvider,
    }

    return Object.entries(this.#config?.provider ?? {})
      .filter(([provider]) => availableProviders[provider])
      .map(([provider, providerConfig]) =>
        availableProviders[provider]({
          assetsModule: this.#assetsModule,
          fetch: this.#fetch,
          config: providerConfig,
          paymentMethodTypes: fiatConfig?.paymentMethodTypes,
          ...fiatConfig?.providers?.[provider],
          ...options,
        })
      )
  }

  fetchAssets = async (parameters = Object.create(null)) => {
    const remoteConfig = await this.#fiatConfigAtom.get()
    const assets = await this.#fiatClient
      .fetchAssets({ ...parameters, useServerData: remoteConfig?.useServerData })
      .catch(() => ({}))

    const countries = Object.fromEntries(this.#fiatClient.countries || [])

    await Promise.all([this.#fiatAssetsAtom.set(assets), this.#fiatCountriesAtom.set(countries)])

    return assets
  }

  getAssetsByPaymentMethod = (assets, { country, orderType, paymentMethod }) =>
    getAssetsByPaymentMethod_(assets, {
      client: this.#fiatClient,
      country,
      orderType,
      paymentMethod,
    })

  #getKeyPair = async () => {
    const configFiatAuthKeyPair = await this.#fiatAuthKeyPairAtom.get()

    if (configFiatAuthKeyPair) {
      return configFiatAuthKeyPair
    }

    const bytes = this.#config.useDeterministicAuthKeyPair
      ? await this.#derivePrivateKey()
      : randomBytes(32)

    const newKeyPair = await sodium.genSignKeyPair(bytes)
    await this.#fiatAuthKeyPairAtom.set(newKeyPair)

    return newKeyPair
  }

  #derivePrivateKey = async () => {
    const { privateKey } = await this.#keychain.exportKey({
      seedId: await this.#wallet.getPrimarySeedId(),
      keyId: EXODUS_KEY_IDS.FIAT_RAMP,
      exportPrivate: true,
    })
    return privateKey
  }

  createOrder = async (order = Object.create(null)) => {
    const getAssetDetails = async ({ assetName, assetTicker, type }) => {
      if (!assetName) {
        return
      }

      const asset = await this.#assetsModule.getAsset(assetName)
      const defaultAsset = assetTicker ?? assetName

      return {
        [`${type}Asset`]: asset ? asset.displayTicker : defaultAsset,
        [`${type}BaseAssetName`]: asset ? asset.baseAssetName : defaultAsset,
      }
    }

    const { fromAsset, fromAssetName, toAsset, toAssetName } = order

    const orderUpdates = await Promise.all([
      getAssetDetails({ assetName: fromAssetName, assetTicker: fromAsset, type: 'from' }),
      getAssetDetails({ assetName: toAssetName, assetTicker: toAsset, type: 'to' }),
    ])

    return this.#fiatClient.createOrder({
      ...order,
      ...Object.fromEntries(orderUpdates.filter(Boolean).flatMap(Object.entries)),
      anonymousId: await this.#analyticsUserIdAtom?.get(),
    })
  }

  debug = async () => {
    const data = await Promise.all([
      this.#fiatAssetsAtom.get(),
      this.#fiatCountriesAtom.get(),
      this.#fiatOrdersAtom.get(),
      this.#fiatUserAtom.get(),
    ])

    return {
      assets: { ...data[0] },
      countries: { ...data[1] },
      orders: data[2].toJSON(),
      user: { ...data[3] },
    }
  }

  getMetaProvider = (subProvider) =>
    [...this.#fiatClient.providers.entries()].find(([, provider]) =>
      provider.providers?.includes(subProvider)
    )?.[0]

  getOrder = async (orderId, order) => {
    const { useServerData } = (await this.#fiatConfigAtom.get()) || {}

    return this.#fiatClient.getOrder(orderId, { ...order, useServerData })
  }

  getProvider = (provider) => this.#fiatClient.providers.get(provider)

  getProviderEnabled = (provider, orderType) => {
    const enabled = this.getProvider(provider)?.enabled
    return Boolean(typeof enabled === 'function' ? enabled({ type: orderType }) : enabled)
  }

  getQuotes = async (parameters = Object.create(null)) => {
    try {
      const remoteConfig = await this.#fiatConfigAtom.get()

      return await this.#fiatClient.getQuotes({
        ...parameters,
        useServerData: remoteConfig?.useServerData,
      })
    } catch (error) {
      this.#logger.warn('Failed to fetch quotes', error)
      throw error
    }
  }

  getServerUrl = () => this.#config.FIAT_SERVER_URL

  getToken = async () => {
    return this.#fiatClient.client.getToken()
  }

  getWidgetUrl = async (
    { fromAsset, paymentMethod, provider, toAsset, type, ...parameters } = Object.create(null)
  ) => {
    const getWidgetUrl = {
      [PROVIDERS.blockchain]: getBlockchainWidgetUrl,
      [PROVIDERS.moonpay]: getMoonpayWidgetUrl,
      [PROVIDERS.onramper]: getOnramperWidgetUrl,
      [PROVIDERS.ramp]: getRampWidgetUrl,
      [PROVIDERS.robinhood]: getRobinhoodWidgetUrl,
      [PROVIDERS.sardine]: getSardineWidgetUrl,
      [PROVIDERS.xopay]: getXopayWidgetUrl,
      [PROVIDERS.paypal]: getPaypalWidgetUrl,
    }

    const metaProvider = this.getMetaProvider(provider)
    const providerName = metaProvider ?? provider
    const getProviderWidgetUrl = getWidgetUrl[providerName]
    if (!getProviderWidgetUrl) {
      throw new Error(`Unknown provider ${providerName}`)
    }

    const providerInstance = this.getProvider(providerName)
    const remoteConfig = await this.#fiatConfigAtom.get()

    const options = {
      ...parameters,
      ...remoteConfig?.providers?.[providerName],
      appId: this.#config.APP_ID,
      config: this.#config.provider[providerName],
      fetchToken: this.#getProviderToken,
      fromAsset,
      provider,
      serverUrl: this.getServerUrl(),
      serverClient: this.#fiatClient.client,
      sign: this.sign,
      signUrl: this.signUrl,
      toAsset,
      type,
    }

    if (paymentMethod) {
      options.paymentMethod = providerInstance.getPaymentMethod(paymentMethod)
    }

    return getProviderWidgetUrl(options, {
      serverClient: this.#fiatClient,
      providerManager: providerInstance,
    })
  }

  hideFailedOrderNotification = async (orderId) => {
    await this.#fiatHiddenFailedOrderNotificationsAtom.set((data) => ({ ...data, [orderId]: true }))
  }

  load = async () => {
    if (this.#loaded) {
      return
    }

    this.#loaded = true

    const [
      { countryCode, ip, regionCode },
      { privateKey, publicKey },
      { adjustedTrustScore } = {},
    ] = await Promise.all([
      this.#geolocationAtom.get(),
      this.#getKeyPair(),
      this.#riskMetricsAtom?.get(),
    ])

    const providers = await this.#getProviders({ trustScore: adjustedTrustScore })

    this.#fiatClient.setOptions({
      geolocation: { countryCode, ip, regionCode },
      providers,
    })

    await this.#fiatClient.authenticate({
      privateKey,
      publicKey,
      sign: async (challenge) =>
        sodium.sign({
          message: Buffer.from(challenge, 'base64'),
          privateKey,
        }),
    })

    this.client = this.#fiatClient.client
    this.#getProviderToken = this.client.getProviderToken.bind(this.client)

    if (this.#riskMetricsAtom) {
      this.#subscriptions.push(
        this.#riskMetricsAtom.observe(({ adjustedTrustScore }) => {
          this.#getProviders({ trustScore: adjustedTrustScore }).then((providers) => {
            this.#fiatClient.setOptions({ providers })
            this.#currencyAtom.get().then((fromAsset) => this.fetchAssets({ fromAsset }))
          })
        })
      )
    }

    this.#subscriptions.push(
      this.#currencyAtom.observe((fromAsset) => this.fetchAssets({ fromAsset }))
    )

    await this.updateUser()
  }

  updateUser = async (...parameters) => {
    const { value: kyc } = await this.#fiatClient.hasUserPassedKYC(...parameters)
    await this.#fiatUserAtom.set({ authenticated: true, kyc })
    return { value: kyc }
  }

  stop = () => {
    this.#loaded = false
    this.#subscriptions.forEach((unsubscribe) => unsubscribe())
    this.#subscriptions = []
  }
}

const fiatRampDefinition = {
  id: 'fiatRamp',
  type: 'module',
  factory: (...args) => new FiatRamp(...args),
  dependencies: [
    'analyticsUserIdAtom?',
    'assetsModule',
    'config',
    'currencyAtom',
    'fiatAssetsAtom',
    'fiatAuthKeyPairAtom',
    'fiatConfigAtom',
    'fiatCountriesAtom',
    'fiatHiddenFailedOrderNotificationsAtom',
    'fiatOrdersAtom',
    'fiatUserAtom',
    'geolocationAtom',
    'getBuildMetadata',
    'logger',
    'fetch',
    'riskMetricsAtom?',
    'fiatClient?',
    'keychain',
    'wallet',
  ],
  public: true,
}

export default fiatRampDefinition
